import { useCallback } from 'react';

import VideoPrivacyIcon from 'assets/images/video-privacy.png';
import { SourceMediaTypes } from 'features/source-media-tracks/enums';
import { useSourceMediaTracks } from 'features/source-media-tracks/hooks';

const PRIVACY_CANVAS_WIDTH = 300;
const PRIVACY_CANVAS_HEIGHT = 150;

interface CachedPrivacyTrack {
  track: CanvasCaptureMediaStreamTrack;
  requestFrame: () => void;
}

// The privacy track is cached at module scope so that toggling privacy on/off never spins up
// a fresh <canvas> + captureStream. Each new captureStream leaks an encoding pipeline for the
// lifetime of the page since RTCRtpSender.replaceTrack does not stop the previous track.
let cachedPrivacyTrack: CachedPrivacyTrack | null = null;

const createPrivacyMediaStreamTrack = (): CachedPrivacyTrack => {
  const canvas = document.createElement('canvas');
  canvas.width = PRIVACY_CANVAS_WIDTH;
  canvas.height = PRIVACY_CANVAS_HEIGHT;
  const context = canvas.getContext('2d');

  // captureStream(0) produces a track that only emits frames when requestFrame() is called,
  // eliminating the continuous ~30fps encoder work that captureStream() defaults to.
  const stream = canvas.captureStream(0);
  const track = stream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack;

  const image = new Image();
  const paintPrivacyFrame = () => {
    if (!context || !image.complete) {
      return;
    }
    context.drawImage(image, 0, 0, PRIVACY_CANVAS_WIDTH, PRIVACY_CANVAS_HEIGHT);
    track.requestFrame?.();
  };
  image.onload = paintPrivacyFrame;
  image.src = VideoPrivacyIcon;
  paintPrivacyFrame();

  return {
    track,
    requestFrame: paintPrivacyFrame,
  };
};

// @todo(PQP): With the new implementation of useSourceMediaTracks, this hook is essentially a straight pass-through
// and we should consider removing it and update the components that use it to use useSourceMediaTracks directly.
export const useMediaStream = () => {
  const { getSourceMediaTrack, refreshSourceMediaTrack } =
    useSourceMediaTracks();

  // Choose video settings based on feature flag resolution value
  // Supports: '720p', '1080p', or undefined/other (defaults to legacy)
  const getInterpAudioStream = useCallback(async () => {
    const interpAudioTrack = await getSourceMediaTrack(
      SourceMediaTypes.INTERP_AUDIO
    );
    return new MediaStream([interpAudioTrack]);
  }, [getSourceMediaTrack]);

  const getInterpVideoStream = useCallback(async () => {
    const interpVideoTrack = await getSourceMediaTrack(
      SourceMediaTypes.INTERP_VIDEO
    );
    return new MediaStream([interpVideoTrack]);
  }, [getSourceMediaTrack]);

  const getFreshInterpVideoStream = useCallback(async () => {
    const interpVideoTrack = await refreshSourceMediaTrack(
      SourceMediaTypes.INTERP_VIDEO
    );
    return new MediaStream([interpVideoTrack]);
  }, [refreshSourceMediaTrack]);

  // Returns a self-view stream backed by a cloned track. The clone shares the same camera source
  // but has an independent lifecycle — stopping it does not affect the original INTERP_VIDEO track,
  // and stopping the original does not affect the clone. This makes the self-view immune to RTC
  // cleanup, BackgroundSelfView unmount, or any other service that stops the managed track.
  const getSelfViewStream = useCallback(async () => {
    const interpVideoTrack = await getSourceMediaTrack(
      SourceMediaTypes.INTERP_VIDEO
    );
    const clonedTrack = interpVideoTrack.clone();
    // Re-enable the cloned track if the original was disabled — the clone inherits
    // the enabled state, but we don't want to mutate the original managed track.
    if (clonedTrack && !clonedTrack.enabled) {
      console.log(
        'Debug: Re-enabling cloned INTERP_VIDEO track for new stream'
      );
      clonedTrack.enabled = true;
    }
    return new MediaStream([clonedTrack]);
  }, [getSourceMediaTrack]);

  const getPrivacyMediaStreamTrack = useCallback(() => {
    if (
      !cachedPrivacyTrack ||
      cachedPrivacyTrack.track.readyState === 'ended'
    ) {
      cachedPrivacyTrack = createPrivacyMediaStreamTrack();
    }

    cachedPrivacyTrack.requestFrame();

    const privacyTrack = cachedPrivacyTrack.track.clone();
    if (!privacyTrack.enabled) {
      privacyTrack.enabled = true;
    }

    Object.defineProperty(privacyTrack, 'mercuryPrivacyTrack', {
      value: true,
      configurable: true,
      enumerable: false,
      writable: false,
    });

    return privacyTrack;
  }, []);

  return {
    getInterpAudioStream,
    getInterpVideoStream,
    getFreshInterpVideoStream,
    getSelfViewStream,
    getPrivacyMediaStreamTrack,
  };
};
